import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

export function useBlockedUsers() {
  const { session } = useAuth();
  const userId = session?.user?.id;
  return useQuery({
    queryKey: ["blocked-users", userId],
    enabled: !!userId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("blocked_users")
        .select("blocked_id")
        .eq("blocker_id", userId!);
      if (error) throw error;
      return new Set(data.map((r) => r.blocked_id));
    },
  });
}

export function useBlockUser() {
  const qc = useQueryClient();
  const { session } = useAuth();
  const userId = session?.user?.id;
  return useMutation({
    mutationFn: async (targetUserId: string) => {
      if (!userId) throw new Error("Sign in required");
      const { error } = await supabase
        .from("blocked_users")
        .insert({ blocker_id: userId, blocked_id: targetUserId });
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["blocked-users", userId] });
      qc.invalidateQueries({ queryKey: ["feed"] });
      qc.invalidateQueries({ queryKey: ["messages"] });
    },
  });
}

export function useUnblockUser() {
  const qc = useQueryClient();
  const { session } = useAuth();
  const userId = session?.user?.id;
  return useMutation({
    mutationFn: async (targetUserId: string) => {
      if (!userId) throw new Error("Sign in required");
      const { error } = await supabase
        .from("blocked_users")
        .delete()
        .eq("blocker_id", userId)
        .eq("blocked_id", targetUserId);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["blocked-users", userId] }),
  });
}
